import {
  CalendarDayEvent,
  getEventOrganizerName,
} from "@/src/utils/calendar-events.utils";
import { useTheme } from "@/src/styles/theme-provider";
import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { Platform, Pressable, Text, View } from "react-native";

const isWeb = Platform.OS === "web";

/** Width of the hover card. Fixed so it can be clamped to the viewport below. */
const CARD_WIDTH = 244;
/** Gap between the day cell and the card, and the minimum margin to the viewport edge. */
const CARD_OFFSET = 6;
const VIEWPORT_MARGIN = 8;
/** Short enough to feel attached to the cursor, long enough not to fire while sweeping across the grid. */
const OPEN_DELAY = 90;
/**
 * The cursor has to cross the gap between the cell and the card. Closing
 * instantly on mouse-leave made the card unreachable — it vanished mid-travel,
 * so the events inside it could never be clicked.
 */
const CLOSE_DELAY = 140;

type CalendarDayCellProps = Readonly<{
  dayNumber?: number;
  isToday: boolean;
  isDisabled: boolean;
  dayEvents: readonly CalendarDayEvent[];
  onSelectEvent: (eventId: string) => void;
}>;

type HoverCardProps = Readonly<{
  dayEvents: readonly CalendarDayEvent[];
  anchor: Readonly<{ x: number; y: number; width: number; height: number }>;
  onSelectEvent: (eventId: string) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}>;

const HoverCard = ({
  dayEvents,
  anchor,
  onSelectEvent,
  onMouseEnter,
  onMouseLeave,
}: HoverCardProps) => {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();

  // Centred under the day, then pulled back inside the window. The calendar
  // lives in the right-hand sidebar, so a card centred on a cell in the last
  // column would otherwise hang off the edge of the page.
  const centered = anchor.x + anchor.width / 2 - CARD_WIDTH / 2;
  const maxLeft =
    (typeof window !== "undefined" ? window.innerWidth : CARD_WIDTH) -
    CARD_WIDTH -
    VIEWPORT_MARGIN;
  const left = Math.max(VIEWPORT_MARGIN, Math.min(centered, maxLeft));

  return createPortal(
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: "fixed",
        top: anchor.y + anchor.height + CARD_OFFSET,
        left,
        width: CARD_WIDTH,
        zIndex: 9999,
        ...(varStyle as React.CSSProperties),
      }}
    >
      <View
        className="rounded-xl border border-ash-3 bg-chalk py-1.5"
        style={{
          shadowColor: themeVars["--color-shadow-dark"] as string,
          shadowOffset: { width: 0, height: 6 },
          shadowOpacity: 1,
          shadowRadius: 12,
          elevation: 20,
        }}
      >
        {dayEvents.map(({ event, dayNumber, totalDays }) => {
          const organizer = getEventOrganizerName(event);
          const isMultiDay = totalDays > 1;
          const startTime = new Date(event.startTime);

          return (
            <Pressable
              // A multi-day event appears once per day cell, so the event id
              // alone is unique within one card. The day is folded in anyway so
              // the key stays stable if a day ever lists an event twice.
              key={`${event.ItemId}-${dayNumber}`}
              onPress={() => onSelectEvent(event.ItemId)}
              className="px-3 py-2 hover:bg-ash-3"
            >
              <View className="flex-row items-start gap-2">
                <Text
                  className="flex-1 text-xs font-semibold text-onyx"
                  numberOfLines={2}
                >
                  {event.title}
                </Text>
                {isMultiDay && (
                  <View className="rounded-full bg-primary-2 px-1.5 py-0.5">
                    <Text className="text-[10px] font-medium text-primary">
                      {t("LABEL_EVENT_DAY_OF", {
                        day: dayNumber,
                        total: totalDays,
                      })}
                    </Text>
                  </View>
                )}
              </View>

              {/* The organiser line is dropped rather than filled with a user
                  id when the event has no organiser snapshot. */}
              {!!organizer && (
                <Text className="mt-0.5 text-[11px] text-slate" numberOfLines={1}>
                  {t("LABEL_ORGANIZER")}: {organizer}
                </Text>
              )}
              <Text className="text-[11px] text-ash-1">
                {format(startTime, "h:mm a")}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </div>,
    document.body,
  );
};

/**
 * A single day in the events calendar.
 *
 * Two things beyond drawing the number: it marks a day that has events with a
 * line (a 1px dot was easy to miss against the digit above it), and on web it
 * opens a card listing that day's events on hover. The card is one card per
 * day, not per event — a day with three events lists three rows — and each row
 * is a link into the event's detail screen.
 *
 * Hover is web-only, as elsewhere in this codebase (see `HoverTooltip`). The
 * calendar itself only renders in the `md:` sidebar, so there is no touch
 * surface to strand: a native build shows the marker line and nothing else.
 */
const CalendarDayCell = ({
  dayNumber,
  isToday,
  isDisabled,
  dayEvents,
  onSelectEvent,
}: CalendarDayCellProps) => {
  const { themeVars } = useTheme();
  const [anchor, setAnchor] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const cellRef = useRef<View>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasEvents = dayEvents.length > 0;

  const clearTimers = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  };

  // A cell can unmount while a timer is in flight — changing month re-renders
  // the whole grid — and firing setState afterwards would leak.
  useEffect(() => clearTimers, []);

  // Re-measured on every open: the sidebar scrolls, so a position captured on
  // first hover would place the card in the wrong spot later on.
  const openCard = () => {
    if (!isWeb || !hasEvents) return;
    clearTimers();
    openTimer.current = setTimeout(() => {
      cellRef.current?.measureInWindow((x, y, width, height) =>
        setAnchor({ x, y, width, height }),
      );
    }, OPEN_DELAY);
  };

  const closeCard = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    openTimer.current = null;
    closeTimer.current = setTimeout(() => setAnchor(null), CLOSE_DELAY);
  };

  const keepCardOpen = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };

  const handleSelectEvent = (eventId: string) => {
    clearTimers();
    setAnchor(null);
    onSelectEvent(eventId);
  };

  const dayTextColor = isToday
    ? "#FFFFFF"
    : isDisabled
      ? (themeVars["--color-ash-default"] as string)
      : (themeVars["--color-onyx-default"] as string);

  return (
    <View
      ref={cellRef}
      className="items-center justify-center py-0.5"
      // @ts-expect-error - web-only hover events, valid via react-native-web
      onMouseEnter={openCard}
      onMouseLeave={closeCard}
    >
      <View
        className="w-6 h-6 rounded-full items-center justify-center"
        style={
          isToday
            ? {
                backgroundColor: themeVars["--color-primary-default"] as string,
              }
            : undefined
        }
      >
        <Text className="text-xs font-medium" style={{ color: dayTextColor }}>
          {dayNumber}
        </Text>
      </View>

      {/* An underline, not a dot: it reads as "this day is marked" at a glance,
          where the 4px dot it replaced was easy to lose under the digit. */}
      <View className="h-2 items-center justify-center">
        {hasEvents && (
          <View
            className="w-4 rounded-full"
            style={{
              height: 3,
              backgroundColor: isDisabled
                ? (themeVars["--color-ash-default"] as string)
                : (themeVars["--color-jade-default"] as string),
            }}
          />
        )}
      </View>

      {anchor && (
        <HoverCard
          dayEvents={dayEvents}
          anchor={anchor}
          onSelectEvent={handleSelectEvent}
          onMouseEnter={keepCardOpen}
          onMouseLeave={closeCard}
        />
      )}
    </View>
  );
};

export default CalendarDayCell;
